/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Provides the public-facing detail view of a Digital Product Passport (DPP).
 * Always uses the public API endpoints and requires no authentication.
 *
 * Main features:
 * - Loads DPP shell data via the public API without authentication
 * - Exposes only the Digital Nameplate submodel for public access
 * - Displays asset metadata (global asset ID, AAS ID) and a thumbnail
 * - Provides a login call-to-action for full authenticated access
 */

import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';

import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {IxModule, ToastService} from '@siemens/ix-angular';

import {DppSubmodelItem} from '../../../../../models/dpp/submodel-item';
import {SubmodelReference} from '../../../../../models/dpp/SubmodelReference';
import {
  getSubmodelIcon,
  getSubmodelMetadata,
  SUBMODEL_CREATE_ROUTE_SEGMENT,
  SubmodelType,
} from '../../../../../models/dpp/submodel-types';
import {DppService} from '../../../../../services/dpp.service';

@Component({
  selector: 'app-public-dpp-detail-view',
  templateUrl: './public-dpp-detail-view.component.html',
  styleUrl: './public-dpp-detail-view.component.scss',
  standalone: true,
  imports: [TranslateModule, IxModule, RouterLink],
})
export class PublicDppDetailViewComponent implements OnInit, OnDestroy {
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly dppService: DppService = inject(DppService);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly translateService: TranslateService =
    inject(TranslateService);

  dppId = '';
  headerTitle = '';
  globalAssetId = '';
  aasId = '';
  thumbnailUrl: string | null = null;
  submodels: DppSubmodelItem[] = [];
  isLoading = true;
  hasError = false;
  defaultSubmodelIcon = 'document';

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.dppId = params['id'];
      this.loadDppData(this.dppId);
    });
  }

  ngOnDestroy(): void {
    this.revokeThumbnailUrl();
  }

  private async loadDppData(dppId: string): Promise<void> {
    try {
      const shellData = await this.dppService.getDppShellPublic(dppId);

      this.aasId = shellData.id || dppId;
      this.globalAssetId = shellData.assetInformation?.globalAssetId || '—';
      this.headerTitle = shellData.displayName?.[0]?.text || `${dppId}`;

      try {
        const file = await this.dppService.getThumbnail(dppId);
        this.revokeThumbnailUrl();
        this.thumbnailUrl = URL.createObjectURL(file.blob);
      } catch {
        this.revokeThumbnailUrl();
        this.thumbnailUrl = null;
      }

      this.submodels = shellData.submodels
        .map((submodel: SubmodelReference) => {
          const metadata = getSubmodelMetadata(submodel.semanticId);
          return {
            id: submodel.id,
            name: metadata?.type,
            version: metadata?.version,
            icon: getSubmodelIcon(metadata?.type, this.defaultSubmodelIcon),
          };
        })
        .filter((s) => s.name === SubmodelType.DIGITAL_NAMEPLATE);
    } catch {
      this.hasError = true;
      await this.toastService.show({
        message: this.translateService.instant(
          'dpp.passport_detail_view.load_error_message',
        ),
        type: 'error',
      });
    } finally {
      this.isLoading = false;
    }
  }

  onSubmodelSelected(submodel?: DppSubmodelItem): void {
    if (
      !submodel?.name ||
      !SUBMODEL_CREATE_ROUTE_SEGMENT[submodel.name as SubmodelType]
    ) {
      return;
    }
    this.router.navigate(['/public/dpp', this.dppId, 'submodel', submodel.id]);
  }

  trackBySubmodel(index: number, item: DppSubmodelItem): string {
    return item.id || item.name || `submodel-${index}`;
  }

  private revokeThumbnailUrl(): void {
    if (this.thumbnailUrl) {
      URL.revokeObjectURL(this.thumbnailUrl);
    }
  }
}
